import { PlusIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { CustomModelPriceDialog } from "@/components/CustomModelPriceDialog";
import { getModelMetadata } from "@/components/ModelDropdown/utils";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useCustomModelPriceSuggestions } from "@/queries/customModelPrices";
import { useModelMetadata } from "@/queries/models/useModelMetadata";

export const SuggestedModelPrices = () => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId
    ? String(userContext.activeWorkspaceId)
    : undefined;

  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  const { data } = useCustomModelPriceSuggestions(auth?.userToken ?? "", workspaceId);
  const { data: metadata } = useModelMetadata(auth?.userToken ?? "");

  const suggestions = data?.suggested_models ?? [];
  if (suggestions.length === 0) {
    return null;
  }

  // Fuzzy-match the picked model against OpenRouter metadata to pre-fill its text price.
  const matched = selectedModel
    ? getModelMetadata("", selectedModel, metadata?.models, metadata?.provider_map)
    : null;
  const initialPrices =
    matched &&
    matched.price_per_1m_tokens_input !== null &&
    matched.price_per_1m_tokens_output !== null
      ? {
          input: { text_tokens: matched.price_per_1m_tokens_input },
          output: { text_tokens: matched.price_per_1m_tokens_output },
        }
      : undefined;

  return (
    <div className="mb-4 rounded-md border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700">Suggested from your usage</h3>
      <p className="mt-1 text-xs text-gray-500">
        You&apos;ve logged these models with no price. Click one to set it.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {suggestions.map(model => (
          <button
            key={model}
            type="button"
            onClick={() => setSelectedModel(model)}
            className="flex items-center gap-1 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs text-blue-600 transition-colors hover:border-blue-400 hover:bg-blue-100 hover:text-blue-700"
          >
            <PlusIcon className="h-3 w-3 shrink-0" />
            {model}
          </button>
        ))}
      </div>
      <CustomModelPriceDialog
        open={selectedModel !== null}
        onOpenChange={open => {
          if (!open) {
            setSelectedModel(null);
          }
        }}
        initialModelName={selectedModel ?? ""}
        initialPrices={initialPrices}
        lockModelName
      />
    </div>
  );
};
